/**
 * NotificationsPage — Batch 3b Scale-ready (Feb 2026)
 *
 * Interview follow-up compliance queue. Recruiters see only interviews
 * they scheduled; admin & hiring_manager see the whole team's pending
 * follow-ups, grouped by scheduler with expandable accordions.
 *
 * Filters: date bucket (All / Overdue / Today / Tomorrow), recruiter
 * dropdown (staff-view only), free-text candidate search. Paginated
 * server-side at 25 per page. Overdue items get a red pill so admins
 * can spot them immediately.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Bell, Calendar, User, Buildings, Check, X as XIcon, MagnifyingGlass,
    Warning, CaretDown, CaretRight,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 25;
const WHEN_OPTIONS = [
    { key: "", label: "Next 48h" },
    { key: "overdue", label: "Overdue" },
    { key: "today", label: "Today" },
    { key: "tomorrow", label: "Tomorrow" },
];

function fmtWhen(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
        weekday: "short", day: "numeric", month: "short",
        hour: "numeric", minute: "2-digit",
    });
}

export default function NotificationsPage() {
    const { user } = useAuth();
    const isStaffView = user?.role === "admin" || user?.role === "hiring_manager";

    // Filters
    const [when, setWhen] = useState("");
    const [recruiterId, setRecruiterId] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [offset, setOffset] = useState(0);

    // Data
    const [items, setItems] = useState([]);
    const [outcomes, setOutcomes] = useState({});
    const [total, setTotal] = useState(0);
    const [overdueCount, setOverdueCount] = useState(0);
    const [rollup, setRollup] = useState(null);
    const [recruiters, setRecruiters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({}); // recruiter accordion state

    // Follow-up modal
    const [active, setActive] = useState(null);
    const [outcome, setOutcome] = useState("");
    const [remark, setRemark] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Debounce search to avoid a fetch per keystroke.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Reset offset whenever a filter changes.
    useEffect(() => { setOffset(0); }, [when, recruiterId, debouncedSearch]);

    // Staff view — load recruiter list once for the dropdown.
    useEffect(() => {
        if (!isStaffView) return;
        api.get("/users/recruiters")
            .then((r) => setRecruiters(r.data || []))
            .catch(() => {});
    }, [isStaffView]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/interviews/upcoming", {
                params: {
                    hours: 48,
                    when: when || undefined,
                    recruiter_id: recruiterId || undefined,
                    search: debouncedSearch || undefined,
                    limit: PAGE_SIZE,
                    offset,
                },
            });
            setItems(r.data.items || []);
            setOutcomes(r.data.outcomes || {});
            setTotal(r.data.total || 0);
            setOverdueCount(r.data.overdue_count || 0);
            if (isStaffView) {
                const c = await api.get("/interviews/followups/compliance");
                setRollup(c.data);
            }
        } catch (e) {
            logError("Failed to load follow-ups:", e);
        } finally {
            setLoading(false);
        }
    }, [when, recruiterId, debouncedSearch, offset, isStaffView]);

    useEffect(() => { load(); }, [load]);

    // Group items by recruiter for the staff accordion view.
    const groupedByRecruiter = useMemo(() => {
        if (!isStaffView) return null;
        const groups = new Map();
        for (const iv of items) {
            const uid = iv.scheduled_by || "unknown";
            const name = iv.scheduler?.name || "Unknown";
            if (!groups.has(uid)) groups.set(uid, { uid, name, items: [] });
            groups.get(uid).items.push(iv);
        }
        return Array.from(groups.values());
    }, [items, isStaffView]);

    const openFollowup = (iv) => {
        setActive(iv);
        setOutcome("");
        setRemark("");
    };

    const submit = async () => {
        if (!active || !outcome) {
            toast.error("Pick an outcome before saving.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(
                `/candidates/${active.candidate_id}/interviews/${active.id}/followup`,
                { outcome, remark: remark || null },
            );
            toast.success("Follow-up recorded.");
            setActive(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save follow-up.");
        } finally {
            setSubmitting(false);
        }
    };

    const outcomeEntries = useMemo(() => Object.entries(outcomes), [outcomes]);

    const renderCard = (iv) => (
        <div
            key={iv.id}
            data-testid={`followup-item-${iv.id}`}
            className={`bg-white border p-4 sm:p-5 transition-colors ${
                iv.is_overdue ? "border-[#FECACA]" : "border-[#E5E5E5] hover:border-ink"
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-heading font-black text-lg tracking-tight">
                            {iv.candidate_name}
                        </p>
                        {iv.is_overdue && (
                            <span
                                data-testid={`followup-overdue-${iv.id}`}
                                className="text-[9px] font-black uppercase tracking-wider bg-[#DC2626] text-white px-1.5 py-0.5 inline-flex items-center gap-1"
                            >
                                <Warning size={9} weight="fill" /> Overdue
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-[#525252] mb-2">
                        <User size={11} weight="bold" className="inline mr-1" />
                        {iv.job_title || "Requirement"}
                        {iv.client_name && (
                            <>
                                <Buildings size={11} weight="bold" className="inline mx-2" />
                                {iv.client_name}
                            </>
                        )}
                    </p>
                    <p className={`text-xs font-bold ${iv.is_overdue ? "text-[#DC2626]" : "text-cobalt"}`}>
                        <Calendar size={11} weight="bold" className="inline mr-1" />
                        {fmtWhen(iv.start)}
                    </p>
                    {iv.scheduler?.name && (
                        <p
                            data-testid={`followup-scheduler-${iv.id}`}
                            className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-1.5"
                        >
                            Scheduled by <span className="text-[#525252]">{iv.scheduler.name}</span>
                        </p>
                    )}
                </div>
                <button
                    data-testid={`followup-mark-${iv.id}`}
                    onClick={() => openFollowup(iv)}
                    className="bg-cobalt hover:bg-cobalt-dark text-white text-xs font-bold uppercase tracking-wider px-4 py-2 whitespace-nowrap"
                >
                    Mark followed up
                </button>
            </div>
        </div>
    );

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

    return (
        <div className="px-4 sm:px-8 py-8 sm:py-10 max-w-5xl mx-auto" data-testid="notifications-page">
            <div className="mb-6">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2 inline-flex items-center gap-1.5">
                    <Bell size={11} weight="bold" /> Interview follow-ups
                    {overdueCount > 0 && (
                        <span
                            data-testid="notifications-overdue-count"
                            className="text-[10px] font-black uppercase tracking-wider bg-[#DC2626] text-white px-1.5 py-0.5 ml-2"
                        >
                            {overdueCount} overdue
                        </span>
                    )}
                </p>
                <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter mb-2">
                    {isStaffView ? "Team compliance queue." : "Your compliance queue."}
                </h1>
                <p className="text-[#525252] max-w-2xl text-sm">
                    {isStaffView
                        ? "Every pending follow-up your team owes in the next 48 hours, grouped by recruiter."
                        : "Interviews in the next 48 hours you haven't yet followed up on."}
                </p>
                <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider mt-2">
                    Sorted: overdue first · longest pending on top
                </p>
            </div>

            {/* Compact filter bar */}
            <div
                data-testid="notification-filters"
                className="flex flex-wrap items-center gap-2 mb-5"
            >
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mr-1 shrink-0">
                    Filters
                </span>
                {WHEN_OPTIONS.map((o) => (
                    <button
                        key={o.key}
                        data-testid={`filter-when-${o.key || "all"}`}
                        onClick={() => setWhen(o.key)}
                        className={`text-xs font-medium px-2.5 h-8 border transition-colors ${
                            when === o.key
                                ? "bg-ink text-white border-ink"
                                : o.key === "overdue" && overdueCount > 0
                                ? "border-[#FECACA] text-[#DC2626] hover:border-[#DC2626]"
                                : "border-[#E5E5E5] text-[#0A0A0A] hover:border-ink"
                        }`}
                    >
                        {o.label}
                        {o.key === "overdue" && overdueCount > 0 && (
                            <span className="ml-1.5 text-[10px] opacity-70">({overdueCount})</span>
                        )}
                    </button>
                ))}
                {isStaffView && (
                    <select
                        data-testid="filter-recruiter"
                        value={recruiterId}
                        onChange={(e) => setRecruiterId(e.target.value)}
                        className="text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none px-2.5 h-8 bg-white cursor-pointer min-w-[130px]"
                    >
                        <option value="">All recruiters</option>
                        {recruiters.map((r) => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </select>
                )}
                <div className="flex-1 max-w-xs relative">
                    <MagnifyingGlass size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                    <input
                        type="text"
                        data-testid="filter-search"
                        placeholder="Search candidate…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full text-xs font-medium border border-[#E5E5E5] text-[#0A0A0A] hover:border-ink focus:border-cobalt focus:outline-none pl-8 pr-2.5 h-8 bg-white"
                    />
                </div>
                <span data-testid="notification-count" className="text-[11px] text-[#A3A3A3] ml-auto">
                    {total} pending
                </span>
            </div>

            {loading ? (
                <div className="text-[#A3A3A3]">Loading…</div>
            ) : items.length === 0 ? (
                <div data-testid="notifications-empty" className="bg-white border border-[#E5E5E5] p-10 text-center">
                    <Check size={30} weight="bold" className="mx-auto text-[#059669] mb-3" />
                    <p className="font-heading text-xl font-bold tracking-tight mb-1">
                        {total === 0 ? "All caught up." : "No matches."}
                    </p>
                    <p className="text-sm text-[#525252]">
                        {total === 0
                            ? "No pending interview follow-ups match this filter."
                            : "Try widening the filter or clear the search."}
                    </p>
                </div>
            ) : isStaffView && groupedByRecruiter ? (
                // Group-by-recruiter accordion for admin/HM
                <div className="space-y-3">
                    {groupedByRecruiter.map((g) => {
                        const isOpen = expanded[g.uid] !== false; // default open
                        const overdueInGroup = g.items.filter((i) => i.is_overdue).length;
                        return (
                            <div
                                key={g.uid}
                                data-testid={`recruiter-group-${g.uid}`}
                                className="bg-white border border-[#E5E5E5]"
                            >
                                <button
                                    onClick={() =>
                                        setExpanded((prev) => ({ ...prev, [g.uid]: !isOpen }))
                                    }
                                    data-testid={`recruiter-group-toggle-${g.uid}`}
                                    className="w-full flex items-center justify-between px-4 sm:px-5 py-3 hover:bg-[#F9FAFB] transition-colors text-left"
                                >
                                    <div className="flex items-center gap-2">
                                        {isOpen ? (
                                            <CaretDown size={14} weight="bold" />
                                        ) : (
                                            <CaretRight size={14} weight="bold" />
                                        )}
                                        <span className="font-bold text-sm">{g.name}</span>
                                        <span className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                                            {g.items.length} pending
                                        </span>
                                        {overdueInGroup > 0 && (
                                            <span className="text-[10px] font-black uppercase bg-[#DC2626] text-white px-1.5 py-0.5">
                                                {overdueInGroup} overdue
                                            </span>
                                        )}
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="border-t border-[#E5E5E5] p-3 sm:p-4 space-y-2">
                                        {g.items.map(renderCard)}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="space-y-3">{items.map(renderCard)}</div>
            )}

            {/* Pagination */}
            {total > PAGE_SIZE && (
                <div
                    data-testid="notification-pagination"
                    className="flex items-center justify-between mt-6 text-xs"
                >
                    <button
                        data-testid="page-prev"
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                        disabled={offset === 0}
                        className="px-3 h-8 border border-[#E5E5E5] disabled:opacity-40 hover:border-ink font-bold uppercase tracking-wider"
                    >
                        ← Prev
                    </button>
                    <span className="text-[#525252]">
                        Page <span className="font-bold text-ink">{currentPage}</span> of {pages}
                    </span>
                    <button
                        data-testid="page-next"
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                        disabled={offset + PAGE_SIZE >= total}
                        className="px-3 h-8 border border-[#E5E5E5] disabled:opacity-40 hover:border-ink font-bold uppercase tracking-wider"
                    >
                        Next →
                    </button>
                </div>
            )}

            {/* Admin/HM 14-day compliance roll-up */}
            {isStaffView && rollup && rollup.rollup.length > 0 && (
                <div className="mt-10" data-testid="followup-compliance">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Team compliance — last 14 days
                    </p>
                    <h2 className="font-heading text-2xl font-black tracking-tighter mb-4">
                        Who&apos;s following up?
                    </h2>
                    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#E5E5E5]">
                        {rollup.rollup.map((r) => {
                            const totalR = r.pending + r.done;
                            const rate = totalR ? Math.round((r.done / totalR) * 100) : 0;
                            return (
                                <div
                                    key={r.user_id}
                                    data-testid={`rollup-row-${r.user_id}`}
                                    className="flex items-center justify-between px-4 sm:px-5 py-3"
                                >
                                    <div>
                                        <p className="font-bold text-sm">{r.user?.name || "Unknown"}</p>
                                        <p className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                                            {r.user?.role || "?"}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-4 text-xs">
                                        <span className="text-[#059669] font-bold">
                                            <Check size={11} weight="bold" className="inline mr-1" /> {r.done} done
                                        </span>
                                        <span className={r.pending > 0 ? "text-[#DC2626] font-bold" : "text-[#525252]"}>
                                            <XIcon size={11} weight="bold" className="inline mr-1" /> {r.pending} pending
                                        </span>
                                        <span className="font-black text-lg tracking-tight text-ink w-14 text-right">
                                            {rate}%
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Follow-up submission modal */}
            <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
                <DialogContent data-testid="followup-dialog">
                    <DialogHeader>
                        <DialogTitle>Record interview follow-up</DialogTitle>
                        <DialogDescription>
                            {active?.candidate_name} — {fmtWhen(active?.start)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <label className="block">
                            <span className="text-xs font-black uppercase tracking-wider text-[#525252] mb-2 block">
                                Outcome *
                            </span>
                            <select
                                data-testid="followup-outcome"
                                value={outcome}
                                onChange={(e) => setOutcome(e.target.value)}
                                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                            >
                                <option value="">Choose an outcome…</option>
                                {outcomeEntries.map(([k, label]) => (
                                    <option key={k} value={k}>{label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-xs font-black uppercase tracking-wider text-[#525252] mb-2 block">
                                Remark (optional)
                            </span>
                            <textarea
                                data-testid="followup-remark"
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                rows={3}
                                placeholder="Anything the team should know?"
                                className="w-full bg-white border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt resize-none"
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <button
                            data-testid="followup-cancel"
                            onClick={() => setActive(null)}
                            className="px-4 py-2 text-sm font-bold uppercase tracking-wider text-[#525252]"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="followup-submit"
                            onClick={submit}
                            disabled={submitting || !outcome}
                            className="bg-cobalt hover:bg-cobalt-dark disabled:opacity-40 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider"
                        >
                            {submitting ? "Saving…" : "Mark done"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
